import React, { useCallback, useMemo, useState } from "react";
import classNames from "classnames";
import { SearchInput } from "@/components/ui/forms/SearchInput";
import type { FormSize } from "@/types";
import styles from "./FilteredList.module.css";

export interface FilteredListProps<T> {
  /** Items to render in the list */
  items: T[];
  /**
   * Filter predicate that receives each item and the normalized query.
   * Normalize (trim/lowercase) inside the component before calling this predicate.
   */
  filterItem: (item: T, normalizedQuery: string) => boolean;
  /** Render prop for each filtered item */
  renderItem: (item: T, index: number) => React.ReactNode;
  /** Placeholder for the search input */
  placeholder?: string;
  /** Initial search query */
  initialQuery?: string;
  /** Optional custom empty state content */
  emptyState?: React.ReactNode;
  /** Optional debounce for the search input (ms) */
  debounce?: number;
  /** Disable the search input (e.g., loading state) */
  searchDisabled?: boolean;
  /** Size variant for the search input */
  size?: FormSize;
  /** Optional read-only test id */
  "data-testid"?: string;
  /** If provided, will be called when the query changes */
  onSearchChange?: (query: string) => void;
  /** Optional container class */
  className?: string;
  /** Optional max height for the list area (enables scrolling). Search stays fixed. */
  maxHeight?: string;
  /**
   * Maximum visible items before scrolling. Alternative to maxHeight.
   * Uses itemHeight to calculate max height (itemHeight * maxItems + gaps).
   */
  maxItems?: number;
  /** Height of each item in pixels (default: 60). Used with maxItems. */
  itemHeight?: number;
  /** Optional heading/title displayed above the list */
  heading?: string;
  /** Optional key extractor for list items */
  itemKey?: (item: T, index: number) => React.Key;
}

export function FilteredList<T>({
  items,
  filterItem,
  renderItem,
  placeholder = "Søk...",
  initialQuery = "",
  emptyState,
  debounce = 0,
  searchDisabled = false,
  size = "md",
  onSearchChange,
  className,
  maxHeight,
  maxItems,
  itemHeight = 60,
  heading,
  itemKey,
  "data-testid": testId,
}: FilteredListProps<T>) {
  const [searchQuery, setSearchQuery] = useState(initialQuery);

  // Calculate effective max height from maxItems if provided
  const effectiveMaxHeight = useMemo(() => {
    if (maxHeight) return maxHeight;
    if (maxItems) {
      // Account for item height + gap (0.1rem ≈ 1.6px between items)
      const gapSize = 1.6;
      const totalHeight = maxItems * itemHeight + (maxItems - 1) * gapSize;
      return `${totalHeight}px`;
    }
    return undefined;
  }, [maxHeight, maxItems, itemHeight]);

  const normalizedQuery = useMemo(
    () => searchQuery.trim().toLowerCase(),
    [searchQuery],
  );

  const filteredItems = useMemo(() => {
    if (!normalizedQuery) {
      return items;
    }
    return items.filter((item) => filterItem(item, normalizedQuery));
  }, [items, filterItem, normalizedQuery]);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchQuery(value);
      onSearchChange?.(value);
    },
    [onSearchChange],
  );

  const emptyContent = emptyState ?? (
    <div
      className={styles.emptyMessage}
      data-testid={testId ? `${testId}-empty` : undefined}
    >
      Ingen treff
    </div>
  );

  return (
    <div
      className={classNames(styles.container, className)}
      data-testid={testId}
    >
      {heading && (
        <div
          className={styles.heading}
          data-testid={testId ? `${testId}-heading` : undefined}
        >
          {heading}
        </div>
      )}
      <div className={styles.searchWrapper}>
        <SearchInput
          placeholder={placeholder}
          value={searchQuery}
          onChange={handleSearchChange}
          onClear={() => handleSearchChange("")}
          disabled={searchDisabled}
          debounce={debounce}
          size={size}
          data-testid={testId ? `${testId}-search` : undefined}
          appearance="clean"
        />
      </div>
      <div
        className={styles.list}
        style={
          effectiveMaxHeight
            ? { maxHeight: effectiveMaxHeight, overflowY: "auto" }
            : undefined
        }
        data-testid={testId ? `${testId}-list` : undefined}
      >
        {filteredItems.length === 0
          ? emptyContent
          : filteredItems.map((item, index) => {
              const key = itemKey ? itemKey(item, index) : index;
              return (
                <React.Fragment key={key}>
                  {renderItem(item, index)}
                </React.Fragment>
              );
            })}
      </div>
    </div>
  );
}

FilteredList.displayName = "FilteredList";

export default FilteredList;
